import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchBySlug, fetchCollection } from '../lib/cms';
import CategoryBadge from '../components/CategoryBadge';
import HelpNotice from '../components/HelpNotice';

export default function OmSkatDetailPage() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [relatedTopics, setRelatedTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadTopic() {
      setLoading(true);
      try {
        let entry = await fetchBySlug(slug);
        if (!entry || entry.collectionName !== 'om_skat') {
          const list = await fetchCollection('om_skat', { limit: 50 });
          entry = (list.items || []).find((t) => (t.slug?._ || t.slug) === slug);
        }
        if (isMounted && entry) {
          setTopic(entry);
          const all = await fetchCollection('om_skat', { limit: 6 });
          setRelatedTopics((all.items || []).filter((t) => t.id !== entry.id).slice(0, 3));
        }
      } catch (e) {
        console.warn('Error loading om_skat topic detail', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadTopic();
    return () => { isMounted = false; };
  }, [slug]);

  const seoTitle = topic?.seo_title || topic?.title || 'Om skat';
  const seoDescription = topic?.seo_description || topic?.summary || '';

  useSeo({
    title: seoTitle,
    description: seoDescription,
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="font-serif text-slate-500 flex items-center gap-2">
          <i className="fa-solid fa-spinner fa-spin text-[#fd7302]" aria-hidden="true" />
          <span>Henter vidensområde...</span>
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white px-4 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#010b27]">
          Emnet blev ikke fundet
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Det efterspurgte vidensområde findes ikke.
        </p>
        <Link
          to="/om-skat"
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#010b27] px-5 py-2.5 text-xs font-semibold text-white hover:bg-[#fd7302]"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til Om skat</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-16">
      {/* Breadcrumbs */}
      <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-1.5 sm:py-2.5 text-[11px] sm:text-xs text-slate-500">
        <div className="mx-auto max-w-7xl flex items-center gap-1.5 sm:gap-2 overflow-hidden">
          <Link to="/" className="hover:text-[#010b27] shrink-0">Forside</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <Link to="/om-skat" className="hover:text-[#010b27] shrink-0">Om skat</Link>
          <span className="text-slate-400 shrink-0">/</span>
          <span className="truncate text-[#010b27] font-medium">{topic.title}</span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-8 sm:pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-12 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="rounded-xs bg-[#fd7302] px-2.5 py-0.5 text-xs font-bold text-white uppercase tracking-wider">
                {topic.topic}
              </span>
              <CategoryBadge audience={topic.audience} size="sm" />
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-black text-[#010b27] leading-tight">
              {topic.title}
            </h1>

            {topic.summary && (
              <div className="my-6 border-l-4 border-[#fd7302] pl-4 sm:pl-6 py-1 bg-slate-50/60 rounded-r-sm">
                <p className="font-serif text-lg sm:text-xl text-[#010b27] italic leading-relaxed">
                  {topic.summary}
                </p>
              </div>
            )}

            {/* Content with clear editorial typography */}
            <div
              className="my-8 prose article-content max-w-none text-[#1e293b]"
              dangerouslySetInnerHTML={{ __html: topic.content || '<p>Indhold under udarbejdelse af redaktionen.</p>' }}
            />
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-xs">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
                <i className="fa-solid fa-book-bookmark text-[#fd7302]" aria-hidden="true" />
                <h3 className="font-serif text-base font-bold text-[#010b27]">
                  Vidensunivers: Om skat
                </h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Opslaget her er en del af Skatunderlups redaktionelle vidensarkiv om skatteregler, fradrag, kontrolprocesser og praksis.
              </p>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <Link
                  to="/om-skat"
                  className="text-xs font-bold text-[#fd7302] hover:underline inline-flex items-center gap-1.5"
                >
                  <span>Se alle skatteemner</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-5 shadow-xs">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
                Personlig skattesag?
              </span>
              <h4 className="mt-1 font-serif text-base font-bold text-[#010b27]">
                Har du modtaget en afgørelse?
              </h4>
              <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                Står du i en konkret tvist med Skattestyrelsen vedrørende {topic.topic.toLowerCase()}, kan du hente uafhængig faglig sparring.
              </p>
              <Link
                to="/kontakt"
                className="mt-4 inline-flex items-center justify-center w-full gap-2 rounded-xs bg-[#fd7302] hover:bg-[#e06500] px-3.5 py-2 text-xs font-bold text-white transition-colors"
              >
                <span>Gå til kontaktformular</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>

        {/* Help Banner */}
        <div className="mt-12">
          <HelpNotice variant="banner" />
        </div>

        {/* Related Topics */}
        {relatedTopics.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200">
            <h3 className="font-serif text-2xl font-bold text-[#010b27] mb-6">
              Andre relevante skatteemner
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedTopics.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/om-skat/${rel.slug?._ || rel.slug || ''}`}
                  className="rounded-md border border-slate-200 bg-white p-4 hover:border-[#fd7302] transition-colors shadow-xs"
                >
                  <span className="text-[10px] font-bold text-[#fd7302] uppercase tracking-wider block mb-1">
                    {rel.topic}
                  </span>
                  <h4 className="font-serif text-base font-bold text-[#010b27] hover:text-[#fd7302] leading-snug">
                    {rel.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
